import type { CxView, GlobalState, TabArgs } from '../types';

import { getCurrentTabId } from '../../util/establishMultitabRole';
import { isChatSuperGroup } from '../helpers';
import { selectChat } from './chats';
import { selectTabState } from './tabs';
import { selectThreadInfo } from './threads';

export function selectCurrentCxWorkspace<T extends GlobalState>(global: T) {
  const { currentWorkspaceId, workspaceById } = global.cx;
  return currentWorkspaceId ? workspaceById[currentWorkspaceId] : undefined;
}

// With a workspace and no chat open, the inbox is the home screen unless the agent picked Teams
const DEFAULT_CX_VIEW: CxView = { type: 'inbox', filter: 'unassigned' };

export function selectCxView<T extends GlobalState>(
  global: T, ...[tabId = getCurrentTabId()]: TabArgs<T>
): CxView | undefined {
  const { cxView, messageLists, cxNavSection } = selectTabState(global, tabId);
  if (cxView) return cxView;
  if (!selectCurrentCxWorkspace(global) || messageLists.length || cxNavSection) return undefined;

  return DEFAULT_CX_VIEW;
}

export function selectCxConversation<T extends GlobalState>(global: T, conversationId: string) {
  return global.cx.conversationById[conversationId];
}

export function selectCxCustomer<T extends GlobalState>(global: T, customerId: string) {
  return global.cx.customerById[customerId];
}

export function selectCxBranchMessages<T extends GlobalState>(global: T, branchId: string) {
  return global.cx.branchMessagesById[branchId];
}

export function selectCxCompany<T extends GlobalState>(global: T, companyId: string) {
  return global.cx.companyById[companyId];
}

export function selectCxWorkspaceTeams<T extends GlobalState>(global: T, workspaceId: string) {
  return global.cx.teamsByWorkspaceId[workspaceId];
}

export function selectIsCxPrivateTeam<T extends GlobalState>(global: T, chatId: string) {
  const workspace = selectCurrentCxWorkspace(global);
  if (!workspace) return false;

  return Boolean(selectCxWorkspaceTeams(global, workspace.id)?.find(({ id }) => id === chatId)?.isPrivate);
}

export function selectCxMembers<T extends GlobalState>(global: T, workspaceId: string) {
  return global.cx.membersByWorkspaceId[workspaceId];
}

export function selectCxCounters<T extends GlobalState>(global: T, workspaceId: string) {
  return global.cx.countersByWorkspaceId[workspaceId];
}

export function selectCxMessages<T extends GlobalState>(global: T, conversationId: string) {
  return global.cx.messagesByConversationId[conversationId];
}

export function selectCxSnippets<T extends GlobalState>(global: T, workspaceId: string) {
  return global.cx.snippetsByWorkspaceId[workspaceId];
}

export function selectCxIntegrations<T extends GlobalState>(global: T, workspaceId: string) {
  return global.cx.integrationsByWorkspaceId[workspaceId];
}

export function selectCxCustomerTypingAt<T extends GlobalState>(global: T, conversationId: string) {
  return global.cx.customerTypingAtByConversationId[conversationId];
}

// Messages of a workspace group whose Sub has replies; deleting such a message deletes its Sub too
export function selectCxSubRootIds<T extends GlobalState>(global: T, chatId: string, messageIds: number[]) {
  const chat = selectChat(global, chatId);
  if (!selectCurrentCxWorkspace(global) || !chat || !isChatSuperGroup(chat) || chat.isForum) return [];

  return messageIds.filter((id) => {
    const threadInfo = selectThreadInfo(global, chatId, id);
    return threadInfo && !threadInfo.isCommentsInfo && Boolean(threadInfo.messagesCount);
  });
}

export function selectCxSubReplyCount<T extends GlobalState>(global: T, chatId: string, messageIds: number[]) {
  return selectCxSubRootIds(global, chatId, messageIds)
    .reduce((count, id) => count + (selectThreadInfo(global, chatId, id)?.messagesCount || 0), 0);
}
